import type { RefObject } from "react";
import { useTranslations } from "use-intl";

import { PhotoLibraryIcon } from "@/components/appIcons";
import { ReleaseCardImageLayer } from "@/components/release-card-image-layer";
import { ReleaseImageLightbox } from "@/components/release-image-lightbox";
import { cn } from "@/lib/utils";
import type { ReleaseImage } from "@garimpa/api/music/getReleaseImages";

export function ReleaseCardImageStackView({
  closeLightbox,
  className,
  containerRef,
  fanned,
  hasGalleryPreview,
  hasKnownImage,
  images,
  lightboxIndex,
  onFocusWithinChange,
  onHoveredChange,
  onOpen,
  previewTriggerOwnerId,
  previewTriggerRef,
  releaseId,
  renderedImageCount,
  resolvedImageCount,
  title,
  totalImageCount,
}: {
  closeLightbox: () => void;
  className?: string;
  containerRef: RefObject<HTMLDivElement | null>;
  fanned: boolean;
  hasGalleryPreview: boolean;
  hasKnownImage: boolean;
  images: ReleaseImage[];
  lightboxIndex: number | null;
  onFocusWithinChange: (value: boolean) => void;
  onHoveredChange: (value: boolean) => void;
  onOpen: (index: number) => void;
  previewTriggerOwnerId: string;
  previewTriggerRef: RefObject<HTMLButtonElement | null>;
  releaseId: string;
  renderedImageCount: number;
  resolvedImageCount: number | null;
  title: string;
  totalImageCount: number;
}) {
  const t = useTranslations("release");
  const imageIndexes = Array.from({ length: renderedImageCount }, (_, index) => index);
  const photoCount = resolvedImageCount ?? totalImageCount;
  const previewLabel = t("viewReleasePhotos", { count: photoCount, title });
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative z-20 aspect-square overflow-hidden bg-muted/50",
        !hasGalleryPreview && "pointer-events-none",
        className,
      )}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") onHoveredChange(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") onHoveredChange(false);
      }}
      onFocusCapture={(event) => {
        if ((event.target as HTMLElement).matches(":focus-visible")) onFocusWithinChange(true);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onFocusWithinChange(false);
      }}
    >
      <div
        className={cn(
          "absolute inset-5 transition-transform duration-300 ease-out motion-reduce:transition-none sm:inset-6",
          fanned && "scale-[1.02]",
        )}
      >
        {imageIndexes.map((imageIndex) => (
          <ReleaseCardImageLayer
            key={imageIndex}
            fanned={fanned}
            hasKnownImage={hasKnownImage}
            imageIndex={imageIndex}
            releaseId={releaseId}
            renderedImageCount={renderedImageCount}
            title={title}
            totalImageCount={totalImageCount}
          />
        ))}
      </div>
      {hasGalleryPreview && (
        <>
          <button
            ref={previewTriggerRef}
            type="button"
            aria-label={previewLabel}
            data-release-gallery-owner={previewTriggerOwnerId}
            title={previewLabel}
            className="absolute inset-0 z-20 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpen(0);
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-1.5 bottom-1.5 z-30 inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border border-border/60 bg-background/90 px-1.5 py-0.5 text-[10px] leading-none font-semibold shadow-sm backdrop-blur-sm"
          >
            <PhotoLibraryIcon decorative style={{ fontSize: "12px", lineHeight: 1 }} />
            {photoCount}
          </span>
        </>
      )}
      {lightboxIndex !== null && (
        <ReleaseImageLightbox
          images={images}
          imageCount={totalImageCount}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
          releaseId={releaseId}
          title={title}
        />
      )}
    </div>
  );
}
